/**
 * 霍兰德职业兴趣测试 - 计算工具
 * 
 * 负责计算Holland Code、归一化分数、类型判定等
 */

import { HOLLAND_TYPES, HOLLAND_CODE_COMBINATIONS } from '@/data/hollandTypes';
import { generateRecommendations } from './hollandRecommendationEngine';

/**
 * 计算原始分数（6个维度，每个0-15分）
 * @param {Array} answers - 答案数组 [{id, dimension, answer}]
 * @returns {Object} 原始分数 {R, I, A, S, E, C}
 */
export function calculateRawScores(answers) {
  const scores = {
    R: 0, // 实际型
    I: 0, // 研究型
    A: 0, // 艺术型
    S: 0, // 社会型
    E: 0, // 企业型
    C: 0  // 传统型
  };
  
  answers.forEach(answer => {
    if (answer.dimension && scores[answer.dimension] !== undefined) {
      scores[answer.dimension] += answer.answer; // 累加0或1
    }
  });
  
  return scores;
}

/**
 * 归一化分数 (将0-15分转换为0-5分)
 * @param {number} rawScore - 原始分数 (0-15)
 * @returns {number} 归一化分数 (0-5)
 */
export function normalizeScore(rawScore) {
  // averageScore = rawScore / 15 * 5
  return parseFloat((rawScore / 15 * 5).toFixed(2));
}

/**
 * 计算百分比分数 (0-100)
 * @param {number} rawScore - 原始分数 (0-15)
 * @returns {number} 百分比 (0-100)
 */
export function calculatePercentage(rawScore) {
  return Math.round((rawScore / 15) * 100);
}

/**
 * 归一化所有维度分数
 * @param {Object} rawScores - 原始分数对象
 * @returns {Object} 归一化和百分比分数对象
 */
export function normalizeAllScores(rawScores) {
  const normalized = {};
  
  Object.keys(rawScores).forEach(dimension => {
    const raw = rawScores[dimension];
    normalized[dimension] = {
      name: HOLLAND_TYPES[dimension]?.fullName || dimension,
      rawScore: raw,
      averageScore: normalizeScore(raw),
      percentage: calculatePercentage(raw),
      level: getScoreLevel(calculatePercentage(raw))
    };
  });
  
  return normalized;
}

/**
 * 生成Holland Code (前3个最高分的维度代码)
 * @param {Object} rawScores - 原始分数对象
 * @returns {string} Holland Code (如 "RIA")
 */
export function generateHollandCode(rawScores) {
  // 按分数降序排序
  const sorted = Object.entries(rawScores)
    .sort((a, b) => b[1] - a[1]);
  
  // 取前3个最高分的维度代码
  return sorted.slice(0, 3).map(item => item[0]).join('');
}

/**
 * 获取主要类型 (Holland Code的第一个字母)
 * @param {string} hollandCode - Holland Code
 * @returns {string} 主要类型代码
 */
export function getPrimaryType(hollandCode) {
  return hollandCode[0] || '';
}

/**
 * 获取次要类型 (Holland Code的第二个字母)
 * @param {string} hollandCode - Holland Code
 * @returns {string} 次要类型代码
 */
export function getSecondaryType(hollandCode) {
  return hollandCode[1] || '';
}

/**
 * 获取第三类型 (Holland Code的第三个字母)
 * @param {string} hollandCode - Holland Code
 * @returns {string} 第三类型代码
 */
export function getTertiaryType(hollandCode) {
  return hollandCode[2] || '';
}

/**
 * 获取分数等级描述
 * @param {number} percentage - 百分比分数
 * @returns {string} 等级描述
 */
export function getScoreLevel(percentage) {
  if (percentage >= 80) return '非常高';
  if (percentage >= 60) return '高';
  if (percentage >= 40) return '中等';
  if (percentage >= 20) return '较低';
  return '低';
}

/**
 * 获取类型详细信息
 * @param {string} hollandCode - Holland Code
 * @returns {Object} 类型信息对象
 */
export function getTypeInfo(hollandCode) {
  const primary = getPrimaryType(hollandCode);
  const secondary = getSecondaryType(hollandCode);
  const tertiary = getTertiaryType(hollandCode);
  
  return {
    primary: HOLLAND_TYPES[primary] || null,
    secondary: HOLLAND_TYPES[secondary] || null,
    tertiary: HOLLAND_TYPES[tertiary] || null
  };
}

/**
 * 获取推荐专业和职业
 * @param {string} hollandCode - Holland Code
 * @returns {Object} 推荐信息
 */
export function getRecommendations(hollandCode) {
  const primary = getPrimaryType(hollandCode);
  const secondary = getSecondaryType(hollandCode);
  const tertiary = getTertiaryType(hollandCode);
  
  return {
    majors: [
      ...(HOLLAND_TYPES[primary]?.majors || []),
      ...(HOLLAND_TYPES[secondary]?.majors || []).slice(0, 2),
      ...(HOLLAND_TYPES[tertiary]?.majors || []).slice(0, 1)
    ],
    careers: [
      ...(HOLLAND_TYPES[primary]?.typicalCareers || []),
      ...(HOLLAND_TYPES[secondary]?.typicalCareers || []).slice(0, 2),
      ...(HOLLAND_TYPES[tertiary]?.typicalCareers || []).slice(0, 1)
    ],
    developmentSuggestions: HOLLAND_TYPES[primary]?.developmentSuggestions || []
  };
}

/**
 * 生成Holland Code解读
 * @param {string} hollandCode - Holland Code
 * @returns {string} 解读文本
 */
export function generateInterpretation(hollandCode) {
  return HOLLAND_CODE_COMBINATIONS.generateInterpretation(hollandCode);
}

/**
 * 完整计算流程
 * @param {Array} answers - 答案数组
 * @param {Object} userInfo - 用户信息 {nickname, gender, age}
 * @returns {Object} 完整的测试结果
 */
export function calculateHollandResult(answers, userInfo = {}) {
  // 1. 计算原始分数
  const rawScores = calculateRawScores(answers);
  
  // 2. 归一化分数
  const dimensionScores = normalizeAllScores(rawScores);
  
  // 3. 生成Holland Code
  const hollandCode = generateHollandCode(rawScores);
  
  // 4. 获取类型信息
  const primaryType = getPrimaryType(hollandCode);
  const secondaryType = getSecondaryType(hollandCode);
  const tertiaryType = getTertiaryType(hollandCode);
  
  // 5. 获取类型详细信息
  const typeInfo = getTypeInfo(hollandCode);
  
  // 6. 获取推荐信息
  const recommendations = getRecommendations(hollandCode);
  
  // 7. 生成解读
  const interpretation = generateInterpretation(hollandCode);
  
  // 8. 生成职业发展建议（6条详细建议）
  const careerRecommendations = generateRecommendations(hollandCode);
  
  // 9. 生成结果对象
  return {
    id: generateResultId(),
    timestamp: new Date().toISOString(),
    userInfo,
    answers,
    rawScores,
    dimensionScores,
    hollandCode,
    primaryType,
    secondaryType,
    tertiaryType,
    typeInfo,
    recommendations, // 旧版推荐（专业、职业列表）
    careerRecommendations, // 新版详细建议（6条）
    interpretation
  };
}

/**
 * 生成结果ID
 * @returns {string} 唯一的结果ID
 */
function generateResultId() {
  return `holland_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * 验证答案完整性
 * @param {Array} answers - 答案数组
 * @returns {boolean} 是否完整
 */
export function validateAnswers(answers) {
  // 应该有90道题的答案
  if (answers.length !== 90) {
    return false;
  }
  
  // 检查每个答案是否有效
  return answers.every(answer => {
    return (
      answer.id !== undefined &&
      answer.dimension !== undefined &&
      (answer.answer === 0 || answer.answer === 1)
    );
  });
}

/**
 * 计算测试进度百分比
 * @param {number} answeredCount - 已回答题目数
 * @returns {number} 进度百分比 (0-100)
 */
export function calculateProgress(answeredCount) {
  const totalQuestions = 90;
  return Math.round((answeredCount / totalQuestions) * 100);
}

/**
 * 格式化测试时长
 * @param {Date} startTime - 开始时间
 * @param {Date} endTime - 结束时间
 * @returns {string} 格式化的时长
 */
export function formatDuration(startTime, endTime) {
  const duration = Math.floor((endTime - startTime) / 1000); // 秒
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  
  if (minutes > 0) {
    return `${minutes}分${seconds}秒`;
  }
  return `${seconds}秒`;
}

/**
 * 获取雷达图数据（6维）
 * @param {Object} dimensionScores - 维度分数对象
 * @returns {Array} 雷达图数据
 */
export function getRadarChartData(dimensionScores) {
  return [
    { 
      dimension: '实际型(R)', 
      value: dimensionScores.R?.rawScore || 0, 
      fullMark: 15,
      percentage: dimensionScores.R?.percentage || 0
    },
    { 
      dimension: '研究型(I)', 
      value: dimensionScores.I?.rawScore || 0, 
      fullMark: 15,
      percentage: dimensionScores.I?.percentage || 0
    },
    { 
      dimension: '艺术型(A)', 
      value: dimensionScores.A?.rawScore || 0, 
      fullMark: 15,
      percentage: dimensionScores.A?.percentage || 0
    },
    { 
      dimension: '社会型(S)', 
      value: dimensionScores.S?.rawScore || 0, 
      fullMark: 15,
      percentage: dimensionScores.S?.percentage || 0
    },
    { 
      dimension: '企业型(E)', 
      value: dimensionScores.E?.rawScore || 0, 
      fullMark: 15,
      percentage: dimensionScores.E?.percentage || 0
    },
    { 
      dimension: '传统型(C)', 
      value: dimensionScores.C?.rawScore || 0, 
      fullMark: 15,
      percentage: dimensionScores.C?.percentage || 0
    }
  ];
}

/**
 * 获取排序后的类型列表
 * @param {Object} rawScores - 原始分数对象
 * @returns {Array} 排序后的类型数组
 */
export function getSortedTypes(rawScores) {
  return Object.entries(rawScores)
    .map(([code, score]) => ({
      code,
      name: HOLLAND_TYPES[code]?.name || code,
      fullName: HOLLAND_TYPES[code]?.fullName || code,
      emoji: HOLLAND_TYPES[code]?.emoji || '',
      color: HOLLAND_TYPES[code]?.color || '#gray',
      score,
      percentage: calculatePercentage(score),
      level: getScoreLevel(calculatePercentage(score)),
      rank: 0 // 将在排序后设置
    }))
    .sort((a, b) => b.score - a.score)
    .map((item, index) => ({
      ...item,
      rank: index + 1
    }));
}

export default {
  calculateRawScores,
  normalizeScore,
  calculatePercentage,
  normalizeAllScores,
  generateHollandCode,
  getPrimaryType,
  getSecondaryType,
  getTertiaryType,
  getScoreLevel,
  getTypeInfo,
  getRecommendations,
  generateInterpretation,
  calculateHollandResult,
  validateAnswers,
  calculateProgress,
  formatDuration,
  getRadarChartData,
  getSortedTypes
};

